/**
 * External Imports
 */
import { FC, useState } from 'react';

/**
 * Internal Imports
 */
import SelectTemplateDropDown from '../../components/searchHeader/selectTemplatesDropdown';
import SideNav from '../../components/sideNav/adminSideNav/AdminSideNav';
import NewMasterTemplate from './templates/newMasterTemplate';
import TemplatesTree from './templates/templatesTree';
import EditMasterTemplate from './templates/editMasterTemplate';

import { useUserRolesRef } from '../../services/UserRoles/userRoles';

/**
 * Global Type Definition Imports
 */
import {
  ICondensedMasterTemplate,
  IFullTemplate
} from '../../config/interfaces';

/**
 * Style Imports
 */
import {
  HeaderItemsContainer,
  StyledTemplateBtn,
  TemplatesTreeBtnContainer,
  TemplatesTreeContainer,
  TemplatesContainer
} from './styles/AdminViews.styles';

/**
 * Constant Imports
 */
import roundingTypes from '../../constants/validRoundingTypes.json';

const Templates: FC<{}> = () => {
  const [selectedTab, setSelectedTab] = useState<string>('patient');
  const [showNewMasterTemplate, setShowNewMasterTemplate] =
    useState<boolean>(false);
  const [showEditMasterTemplate, setShowEditMasterTemplate] =
    useState<boolean>(false);
  const [selectedMasterTemplate, setSelectedMasterTemplate] = useState<
    ICondensedMasterTemplate | IFullTemplate | null
  >(null);
  const [selectedTemplateDocId, setSelectedTemplateDocId] =
    useState<string>('');

  const { isEnterpriseAdmin } = useUserRolesRef();

  const handleEditTemplate = () => {
    if (selectedMasterTemplate?.templateConfigId) {
      setSelectedTemplateDocId(
        `template:${selectedMasterTemplate?.templateConfigId}:master:HCA`
      );
      // appInsights.trackEvent({
      //   name: 'Master_Template_Edited'
      // });
    }
    setShowEditMasterTemplate(true);
  };

  const handleNewMasterTemplate = () => {
    setShowNewMasterTemplate(true);
    // appInsights.trackEvent({
    //   name: 'Master_Template_Started'
    // });
  };

  const menuItems = [
    {
      tabLabel: 'Patient Templates',
      tabId: 'patient',
      roundingTypes: ['patient']
    },
    {
      tabLabel: 'Employee Templates',
      tabId: 'employee',
      roundingTypes: ['employee']
    },
    {
      tabLabel: 'Support Templates',
      tabId: 'support',
      roundingTypes: ['csc', 'cned']
    },
    {
      tabLabel: 'Validation Templates',
      tabId: 'validation',
      roundingTypes: ['validation']
    },
    {
      tabLabel: 'AIDET® Templates',
      tabId: 'aidetValidation',
      roundingTypes: ['aidetValidation']
    },
    {
      tabLabel: 'BSSR Templates',
      tabId: 'bssrValidation',
      roundingTypes: ['bssrValidation']
    }
  ];

  return (
    <TemplatesContainer>
      {!showEditMasterTemplate && (
        <>
          <SideNav
            navItems={menuItems}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            showNewMasterTemplate={showNewMasterTemplate}
          />
          <TemplatesTreeContainer>
            <HeaderItemsContainer>
              {!showNewMasterTemplate && (
                <SelectTemplateDropDown
                  type="templateTree"
                  roundingType={selectedTab}
                  selectedMasterTemplate={selectedMasterTemplate}
                  setSelectedMasterTemplate={setSelectedMasterTemplate}
                />
              )}
              {isEnterpriseAdmin && (
                <TemplatesTreeBtnContainer className="d-flex justify-content-end">
                  {!showNewMasterTemplate && (
                    <StyledTemplateBtn
                      data-testid="edit-template-button"
                      class="me-3"
                      color="blue"
                      icon="edit"
                      kind="primary"
                      size="small"
                      onClick={handleEditTemplate}
                    >
                      Edit Master Template
                    </StyledTemplateBtn>
                  )}
                  {!showNewMasterTemplate && (
                    <StyledTemplateBtn
                      data-testid="add-template-button"
                      class="me-3"
                      color="blue"
                      icon="add"
                      kind="primary"
                      size="small"
                      style={{ marginLeft: 'auto' }}
                      onClick={handleNewMasterTemplate}
                    >
                      New Master Template
                    </StyledTemplateBtn>
                  )}
                </TemplatesTreeBtnContainer>
              )}
            </HeaderItemsContainer>
            {!showNewMasterTemplate && (
              <TemplatesTree
                selectedMasterTemplate={selectedMasterTemplate}
                setSelectedTemplateDocId={setSelectedTemplateDocId}
                setShowEditTemplateFlag={setShowEditMasterTemplate}
              />
            )}
            {showNewMasterTemplate && (
              <NewMasterTemplate
                menuItemsMinusLabels={menuItems.map(
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  ({ tabLabel, ...rest }) => rest
                )}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                setSelectedMasterTemplate={setSelectedMasterTemplate}
                setShowNewMasterTemplate={setShowNewMasterTemplate}
                templateTypes={roundingTypes}
              />
            )}
          </TemplatesTreeContainer>
        </>
      )}
      {showEditMasterTemplate && selectedTemplateDocId && (
        <EditMasterTemplate
          selectedTemplateDocId={selectedTemplateDocId}
          setSelectedMasterTemplate={setSelectedMasterTemplate}
          setShowEditTemplateFlag={setShowEditMasterTemplate}
        />
      )}
    </TemplatesContainer>
  );
};

export default Templates;
